import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Banner, CloseButton } from '@zillow/constellation-10';

import BlockedUserModal from '@zg-rentals/blocked-user-modal';
import { useZrmPageViewEvents } from '@zg-rentals/clickstream';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { RequestWidget } from '@zg-rentals/rental-manager-env';
import { RpErrorBoundary } from '@zg-rentals/rp-bootstrap-browser';
import { useSiftScience } from '@zg-rentals/sift-science';
import { useRentalManagerUserUnifiedLeadInsertIntentionActionWebApiHandler_insertAction } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { AppLayoutContainer } from '@zg-rentals/zrm-header-navigation';

import cacheStorageUtils from '../../client/workers/utils/cacheStorage';
import { useIsInMobileWebView, useIsWizardView } from '../../hooks';
import { getCookieAsBoolean } from '../../utils/cookieUtils';
import { mapBrandStringToEnum, mapIntentionTypeStringToEnum } from '../../utils/inquiryContactUtils';

import { ERROR_PAGE_TYPES } from '../../constants/ErrorMessageConstants';
import { ZRM_IA_NAV_ENABLED } from '../../constants/FeatureFlagConstants';
import { EXTERNAL_ROUTES, ROUTES } from '../../constants/urls';

import { trackComscoreEvent, trackEvent } from '../../analytics';
import { useAuth } from '../AuthProvider';
import ErrorPage from '../ErrorPage';

type LayoutProps = {
  children: React.ReactNode;
};

export default function Layout({ children }: LayoutProps) {
  const router = useRouter();
  const prevPath = useRef<string>('');

  const { user } = useAuth();
  const isInMobileWebView = useIsInMobileWebView();
  const isWizardView = useIsWizardView();

  const [displayOfflineNotification, setDisplayOfflineNotification] = useState(false);
  const [displayBlockedUserModal, setDisplayBlockedUserModal] = useState(false);

  const hostName = typeof window !== 'undefined' ? window.location.host : 'www.zillow.com';

  const hideNavigation = isInMobileWebView === true || isWizardView === true;

  const userCanAccessRequestWidget =
    (user?.isMultifamilyCustomer || user?.isSingleFamilyEnterpriseCustomer) &&
    !router.pathname.includes(ROUTES.SUBMIT_REQUEST) &&
    !router.pathname.includes(ROUTES.LISTING);

  useSiftScience({ email: user?.email, isImpersonator: Boolean(user?.impersonatorUserToken) });
  useZrmPageViewEvents(trackEvent.user, router.pathname, user);
  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);

  const { mutate: insertLeadAction } = useRentalManagerUserUnifiedLeadInsertIntentionActionWebApiHandler_insertAction({
    meta: {
      context: {
        api: 'rental-manager',
        details: 'useRentalManagerUserUnifiedLeadInsertIntentionActionWebApiHandler_insertAction',
      },
      invalidates: ['users-intention'],
    },
  });

  const handleOffline = () => setDisplayOfflineNotification(true);
  const handleOnline = () => setDisplayOfflineNotification(false);

  useEffect(() => {
    if (!user?.loggedIn) {
      cacheStorageUtils.clear();
    }
  }, [user?.loggedIn]);

  // If session expired from 30 minutes of inactivity, while impersonating, we need to redirect user to the login page
  // Locally, this will cause an infinite redirect, so only doing this in production
  useEffect(() => {
    const isProduction = process.env.NODE_ENV === 'production';
    if (isProduction && user?.impersonatorEmail && user?.impersonatorUserToken && !user?.loggedIn) {
      window.location.href = EXTERNAL_ROUTES.SIGN_IN;
    }
  }, [user?.impersonatorEmail, user?.impersonatorUserToken, user?.loggedIn, router]);

  useEffect(() => {
    if (user && !user?.restrictions?.isAllowed) {
      if (
        router.pathname.toLowerCase().includes(ROUTES.SUBMIT_REQUEST) ||
        router.pathname.toLowerCase().includes(ROUTES.FEATURE_FLAGS) ||
        router.pathname.toLowerCase().includes(ROUTES.SECURITY_INQUIRY)
      ) {
        setDisplayBlockedUserModal(false);
      } else {
        setDisplayBlockedUserModal(true);
        if (router.pathname !== ROUTES.PROPERTIES) {
          router.replace(ROUTES.PROPERTIES);
        }
      }
    }

    prevPath.current = router.pathname;
  }, [user?.restrictions?.isAllowed, router]);

  useEffect(() => {
    cacheStorageUtils.clearStaleUserSessions(user?.userToken);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user?.userToken]);

  useEffect(() => {
    const { inquiryId, intentionType, controlHash, utm_medium, landlordBrand, renterBrand } = router.query;
    const mappedIntentionType = mapIntentionTypeStringToEnum(String(intentionType));

    if (inquiryId && controlHash && mappedIntentionType && utm_medium && utm_medium === 'email') {
      const landlordBrandEnum = mapBrandStringToEnum(String(landlordBrand));
      const renterBrandEnum = mapBrandStringToEnum(String(renterBrand));

      insertLeadAction({
        postData: {
          intentionType: mappedIntentionType,
          inquiryId: Number(inquiryId),
          controlHash: String(controlHash),
          landlordBrand: landlordBrandEnum,
          renterBrand: renterBrandEnum,
        },
      });
    }
  }, [router.query, insertLeadAction]);

  return (
    <>
      {displayOfflineNotification && (
        <Banner
          tone="warning"
          closeButton={<CloseButton onImpact />}
          onClose={() => setDisplayOfflineNotification(false)}
        >
          You are browsing offline. Please check your internet connection.
        </Banner>
      )}
      {user != null && (
        <AppLayoutContainer
          altLogo={!user?.isEnterpriseCustomer}
          brand={'zillow'}
          userModel={user}
          reactRouterLink={Link}
          host={hostName}
          pathname={typeof window !== 'undefined' ? window.location.href : ''}
          displayMainMyProperties={true}
          hideNavigation={hideNavigation}
          hideFooter={isWizardView || isInMobileWebView}
          // ZRM-16458: opt into the new IA nav via the QA cookie. Read synchronously
          // here (the nav is client-gated on `user`, so it never SSRs) to give
          // AppLayoutContainer the value on first render — flash-free, no prop drilling.
          iaNavEnabled={getCookieAsBoolean(ZRM_IA_NAV_ENABLED)}
        >
          <div data-testid="zrmw-next-layout-container">
            <RpErrorBoundary fallback={<ErrorPage type={ERROR_PAGE_TYPES.TECHNICAL_DIFFICULTIES} />}>
              {children}
            </RpErrorBoundary>
          </div>
        </AppLayoutContainer>
      )}
      {userCanAccessRequestWidget && (
        <RequestWidget
          zrmwUser={user}
          routerLocation={{ pathname: router.pathname, search: router.query.toString() }}
        />
      )}
      {displayBlockedUserModal && user != null && user.restrictions != null && <BlockedUserModal user={user} />}
    </>
  );
}
